import { t, type TranslationKey } from './i18n';

export type Route = {
  /** id of the <section class="screen"> this page owns */
  screen: string;
  titleKey: TranslationKey;
  /** nav item highlighted for this route (defaults to the path) */
  nav?: string;
  mount: (params: URLSearchParams) => void | Promise<void>;
  unmount?: () => void;
};

const routes = new Map<string, Route>();
let current: { path: string; route: Route } | null = null;

export function registerRoute(path: string, route: Route): void {
  routes.set(path, route);
}

export function setTitle(text: string | null): void {
  const base = t('meta.title');
  document.title = text ? `${text} · gAz` : base;
}

function normalize(path: string): string {
  return path.length > 1 ? path.replace(/\/+$/, '') : path;
}

async function render(): Promise<void> {
  const path = normalize(location.pathname);
  const route = routes.get(path);
  if (!route) {
    navigate('/', true);
    return;
  }

  if (current) current.route.unmount?.();
  current = { path, route };

  document.querySelectorAll<HTMLElement>('.screen').forEach((el) => {
    const active = el.id === route.screen;
    el.classList.toggle('hidden', !active);
    if (active) {
      el.classList.remove('screen-enter');
      void el.offsetWidth; // restart the enter animation
      el.classList.add('screen-enter');
    }
  });

  const navPath = route.nav ?? path;
  document.querySelectorAll<HTMLElement>('[data-nav]').forEach((el) => {
    const on = el.dataset.nav === navPath;
    el.classList.toggle('active', on);
    if (on) el.setAttribute('aria-current', 'page');
    else el.removeAttribute('aria-current');
  });

  setTitle(path === '/' ? null : t(route.titleKey));
  document.body.dataset.route = path === '/' ? 'home' : path.slice(1);
  document.dispatchEvent(new CustomEvent('route:changed', { detail: path }));

  try {
    await route.mount(new URLSearchParams(location.search));
  } catch (err) {
    console.error('[router] mount failed', err);
  }
}

export function navigate(url: string, replace = false): void {
  const target = new URL(url, location.origin);
  const same = target.pathname === location.pathname && target.search === location.search;
  const depth = (history.state as { __navDepth?: number } | null)?.__navDepth ?? 0;
  if (replace || same) history.replaceState({ __navDepth: depth }, '', target.pathname + target.search + target.hash);
  else history.pushState({ __navDepth: depth + 1 }, '', target.pathname + target.search + target.hash);
  if (!target.hash) window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
  void render();
}

/** Goes back if we navigated inside the SPA, otherwise to a fallback path. */
export function goBack(fallback: string): void {
  const depth = (history.state as { __navDepth?: number } | null)?.__navDepth ?? 0;
  if (depth > 0) history.back();
  else navigate(fallback);
}

/** Re-mounts the current page (used after locale change). */
export function rerender(): void {
  if (!current) return;
  const y = window.scrollY;
  current.route.unmount?.();
  void Promise.resolve(current.route.mount(new URLSearchParams(location.search))).then(() => {
    window.scrollTo({ top: y, behavior: 'instant' as ScrollBehavior });
  });
  setTitle(current.path === '/' ? null : t(current.route.titleKey));
}

export function startRouter(): void {
  document.addEventListener('click', (e) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const a = (e.target as Element | null)?.closest?.('a');
    if (!a || !a.href) return;
    if (a.target === '_blank' || a.hasAttribute('download') || a.dataset.external !== undefined) return;
    const url = new URL(a.href);
    if (url.origin !== location.origin || url.pathname.startsWith('/api')) return;
    if (url.pathname === location.pathname && url.search === location.search && url.hash) return;
    e.preventDefault();
    navigate(url.pathname + url.search + url.hash);
  });

  window.addEventListener('popstate', () => void render());
  if (!history.state) history.replaceState({ __navDepth: 0 }, '', location.href);
  void render();
}
